import _ from 'lodash'
import * as React from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { selectScheduleTypesStatus } from 'slices/scheduleTypesSlice'
import { selectSkillsStatus } from 'slices/skillsSlice'

import { FilteringButton, FilteringInputField } from 'components/common'
import type { FilterItem } from 'components/common/types'
import { SUPPORT_SCHEDULE_TYPE_ID } from 'components/common/utils'

import styles from './WorkPlanFilter.module.scss'

import type { EditGroupsType } from '../types'

type Props = {
  onChange: React.Dispatch<React.SetStateAction<EditGroupsType[]>>
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>
  shouldReset: boolean
  setShouldReset: React.Dispatch<React.SetStateAction<boolean>>
}
// 同じKeyになり、チェックボックスの未設定の動作に不具合が出るので、分ける
const SKILL_BLANK_ID = -2
const SKILL_BLANK_LABEL = '未設定'
const SCHEDULE_TYPE_BLANK_ID = -1
const SCHEDULE_TYPE_BLANK_LABEL = '未設定'
const WorkPlanFilter: React.FC<Props> = ({ onChange, onOpenChange, shouldReset, setShouldReset }) => {
  const [openFilter, setOpenFilter] = React.useState(false)
  const { skills } = useSelector(selectSkillsStatus, shallowEqual)
  const { partialScheduleTypes } = useSelector(selectScheduleTypesStatus, shallowEqual)
  const [filterWord, setFilterWord] = React.useState('')
  const [selectedFilterScheduleTypes, setSelectedFilterScheduleTypes] = React.useState<number[]>([])
  const [selectedFilterSkills, setSelectedFilterSkills] = React.useState<number[]>([])

  React.useEffect(
    () =>
      partialScheduleTypes &&
      setSelectedFilterScheduleTypes(partialScheduleTypes.map(g => g.id).concat(SCHEDULE_TYPE_BLANK_ID)),
    [partialScheduleTypes]
  )

  React.useEffect(() => skills && setSelectedFilterSkills(skills.map(s => s.id).concat(SKILL_BLANK_ID)), [skills])

  React.useEffect(() => {
    if (!shouldReset) {
      return
    }
    skills && setSelectedFilterSkills(skills.map(s => s.id).concat(SKILL_BLANK_ID))
    partialScheduleTypes &&
      setSelectedFilterScheduleTypes(partialScheduleTypes.map(g => g.id).concat(SCHEDULE_TYPE_BLANK_ID))
    setFilterWord('')
    setShouldReset(false)
  }, [shouldReset, setShouldReset, skills, partialScheduleTypes])

  const filterScheduleTypeItems = React.useMemo<FilterItem[]>(
    () =>
      partialScheduleTypes
        .map(s => ({
          key: s.id,
          label: s.name,
          checked: selectedFilterScheduleTypes.includes(s.id),
        }))
        .concat({
          key: SCHEDULE_TYPE_BLANK_ID,
          label: SCHEDULE_TYPE_BLANK_LABEL,
          checked: selectedFilterScheduleTypes.includes(SCHEDULE_TYPE_BLANK_ID),
        }),
    [partialScheduleTypes, selectedFilterScheduleTypes]
  )

  const filterSkillItems = React.useMemo<FilterItem[]>(
    () =>
      skills
        .map(s => ({ key: s.id, label: s.name, checked: selectedFilterSkills.includes(s.id) }))
        .concat({
          key: SKILL_BLANK_ID,
          label: SKILL_BLANK_LABEL,
          checked: selectedFilterSkills.includes(SKILL_BLANK_ID),
        }),
    [skills, selectedFilterSkills]
  )

  React.useEffect(() => {
    onChange(prev =>
      prev.map(g => {
        const workers = g.workers.map(w => {
          const workerSchedules = w.schedules
            .map(s => s.scheduleTypeId)
            .filter(s => s !== SCHEDULE_TYPE_BLANK_ID && s !== SUPPORT_SCHEDULE_TYPE_ID)

          const hasBlankScheduleTypes =
            selectedFilterScheduleTypes.includes(SCHEDULE_TYPE_BLANK_ID) && _.isEmpty(workerSchedules)
          const hasScheduleTypes =
            hasBlankScheduleTypes || !_.isEmpty(_.intersection(selectedFilterScheduleTypes, workerSchedules))

          const hasBlankSkill = selectedFilterSkills.includes(SKILL_BLANK_ID) && _.isEmpty(w.skillIds)
          const hasSelectedSkill = hasBlankSkill || !_.isEmpty(_.intersection(selectedFilterSkills, w.skillIds))

          const hasFilterWord = filterWord === '' || w.name.includes(filterWord) || w.wmsMemberId?.includes(filterWord)

          return { ...w, visible: hasScheduleTypes && hasSelectedSkill && hasFilterWord }
        })

        return { ...g, workers }
      })
    )
  }, [selectedFilterScheduleTypes, selectedFilterSkills, filterWord, onChange])
  return (
    <div>
      <div className="d-flex align-items-center">
        <div className="me-2">
          <FilteringInputField onChange={setFilterWord} placeholder="名前 / IDで検索" value={filterWord} />
        </div>
        <button
          className={`d-flex align-items-center justify-content-center  ${
            openFilter ? styles.openIconButton : styles.closeIconButton
          }`}
          color="secondary"
          onClick={() => {
            setOpenFilter(prev => {
              onOpenChange(!prev)
              return !prev
            })
          }}
        >
          <i className="icf-filter font-large" />
        </button>
      </div>
      {openFilter && (
        <div className="d-flex mt-2">
          <div className="me-2">
            <FilteringButton
              items={filterScheduleTypeItems}
              onChange={setSelectedFilterScheduleTypes}
              value={selectedFilterScheduleTypes}
              label="作業で絞り込み"
              disabled={_.isEmpty(partialScheduleTypes)}
            />
          </div>
          <FilteringButton
            items={filterSkillItems}
            onChange={setSelectedFilterSkills}
            value={selectedFilterSkills}
            label="スキルで絞り込み"
            disabled={_.isEmpty(skills)}
          />
        </div>
      )}
    </div>
  )
}

export default WorkPlanFilter
